import React, { useEffect, useMemo, useState } from 'react';
import { api, moneyFull, pct } from '../../lib/api';
import { KpiCard, Callout } from '../../components/UI';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ShieldCheck, RefreshCw, Sparkles, CalendarClock, Activity,
  ListChecks, Search, BookOpenCheck,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  STATUS_COLORS, DECISION_OPTIONS, ACTIONABLE_STATUSES, PASSTHROUGH_LABELS,
  formatSpend, CHART_GRID, CHART_AXIS, CHART_TOOLTIP,
  Th, FilterSelect,
} from './_shared';
import { VendorDetail } from './VendorDetail';
import { HistoryView } from './HistoryView';
import { AiRecommendationsView } from './AiRecommendationsView';

const SUBVIEWS = [
  { key: 'dashboard', label: 'Audit Dashboard', icon: Activity },
  { key: 'vendors', label: 'Vendors', icon: ListChecks },
  { key: 'workflow', label: 'Quarterly Audit', icon: ShieldCheck },
  { key: 'ai', label: 'AI Recommendations', icon: Sparkles },
  { key: 'history', label: 'History', icon: BookOpenCheck },
];

export default function ExpenseAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [view, setView] = useState('dashboard');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [ai, setAi] = useState(null);
  const [hideTrust, setHideTrust] = useState(() => localStorage.getItem('ea-hide-trust') === '1');

  const fetchData = (hide = hideTrust) => {
    setLoading(true);
    api.get(`/expense-audit?hide_trust=${hide ? 'true' : 'false'}`)
      .then((r) => {
        setData(r.data);
        // Keep selectedVendor in sync with latest payload so the detail panel
        // reflects fresh override/decision state after a save.
        setSelectedVendor((prev) => {
          if (!prev) return prev;
          const fresh = (r.data.vendors || []).find((v) => v.id === prev.id);
          return fresh || prev;
        });
      })
      .catch(() => toast.error('Could not load expense audit.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    api.get('/ai/expense-audit/latest').then((r) => setAi(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleHideTrust = (next) => {
    setHideTrust(next);
    localStorage.setItem('ea-hide-trust', next ? '1' : '0');
    fetchData(next);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/integrations/expense-audit/sync');
      const v = r.data.vendor_master;
      const e = r.data.expense_detail;
      if (v?.ok || e?.ok) {
        toast.success(
          `Synced. ${v?.ok ? `${v.count} vendors` : 'vendor sheet skipped'} · ${e?.ok ? `${e.count} transactions` : 'expense sheet skipped'}`,
        );
        fetchData();
      } else {
        toast.error('Both sheets are unconfigured. Add IDs to the .env file first.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="label-editorial">Loading expense audit…</div>;

  const configured = data?.configured || {};
  const isAnyConfigured = configured.vendor_master_configured || configured.expense_detail_configured;

  return (
    <div data-testid="expense-audit-page" className="space-y-7">
      <Header
        kpis={data?.kpis}
        quarterKey={data?.current_quarter_key}
        syncing={syncing}
        onSync={handleSync}
        hideTrust={hideTrust}
        onToggleTrust={toggleHideTrust}
      />

      <SopHeader />

      {!isAnyConfigured && <EmptyStatePanel serviceAccount={configured.service_account_email} />}

      <Subnav view={view} onChange={setView} />

      {view === 'dashboard' && (
        <AuditDashboard
          data={data}
          ai={ai}
          onSelectVendor={(v) => { setSelectedVendor(v); setView('vendors'); }}
          onGoTo={setView}
          onDataChanged={fetchData}
        />
      )}
      {view === 'vendors' && (
        <VendorsView
          data={data}
          selectedVendor={selectedVendor}
          setSelectedVendor={setSelectedVendor}
          onDecisionSaved={fetchData}
        />
      )}
      {view === 'workflow' && (
        <QuarterlyAuditView
          data={data}
          selectedVendor={selectedVendor}
          setSelectedVendor={setSelectedVendor}
          onDecisionSaved={fetchData}
          onFreeze={fetchData}
        />
      )}
      {view === 'ai' && (
        <AiRecommendationsView ai={ai} setAi={setAi} hasData={(data?.vendors?.length || 0) > 0 || (data?.unmapped?.length || 0) > 0} />
      )}
      {view === 'history' && <HistoryView data={data} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quarterly Audit SOP header — collapsible card that explains the 4-week
// rhythm. Default collapsed; the expand state is persisted in localStorage so
// once a user opens it, it stays open for them. Branded per Indie Law spec:
// navy header band, warm-white body, yellow accent stripe.
// ---------------------------------------------------------------------------
function SopHeader() {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem('ea-sop-expanded') === '1'; } catch { return false; }
  });
  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem('ea-sop-expanded', next ? '1' : '0'); } catch {}
  };
  return (
    <div
      data-testid="ea-sop-header"
      className="border-t-4"
      style={{ borderTopColor: '#ffd100' }}
    >
      <button
        type="button"
        onClick={toggle}
        data-testid="ea-sop-toggle"
        className="w-full text-left px-6 py-3 flex items-center justify-between gap-4 transition-colors"
        style={{ background: '#141b4d', color: '#f2e9db' }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-70" style={{ fontFamily: 'Arial, sans-serif' }}>
            How We Run the Quarterly Vendor Audit
          </div>
          <div className="text-sm mt-0.5" style={{ fontFamily: 'Arial, sans-serif' }}>
            The 4-week rhythm that keeps every dollar accountable. {expanded ? '' : <span className="opacity-80">Click to expand.</span>}
          </div>
        </div>
        <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#ffd100' }}>
          {expanded ? '— Collapse' : '+ Expand'}
        </div>
      </button>
      {expanded && (
        <div
          data-testid="ea-sop-body"
          className="px-8 py-8 space-y-7"
          style={{ background: '#f2e9db', color: '#141b4d', fontFamily: 'Arial, sans-serif' }}
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">The Indie Law Quarterly Vendor Audit</div>
            <div className="text-2xl font-bold mt-1">The 4-week rhythm that keeps every dollar accountable.</div>
          </div>
          <p className="text-sm leading-relaxed max-w-3xl">
            Every quarter, we run through every vendor pulling money out of Indie Law. Not to cut for the sake of cutting.
            To make sure every dollar is still earning its keep and every recurring charge has a human who owns it.
          </p>
          <p className="text-sm italic opacity-80">Here's how it works.</p>

          <SopWeek
            label="Week 1 — Review"
            owner="Anu"
            body={[
              "Open the AI Recommendations tab and read the 60-second summary. Skim the Top Opportunities. Note the dollar impact of each.",
              "Bring the top 3 to 5 opportunities to the next leadership meeting as a one-page brief. Include: what the report is recommending, the estimated annual impact, and the vendors involved.",
            ]}
          />
          <SopWeek
            label="Week 2 — Decide"
            owner="Joey, with Anu and Steve"
            body={[
              "In a 20-minute block at the leadership meeting, review each opportunity. For every one, make one of four calls:",
            ]}
            list={[
              "Execute (assign an owner and a due date)",
              "Modify (revise the recommendation, then assign)",
              "Defer (note the reason; revisit next quarter)",
              "Reject (note the reason so the next report has the context)",
            ]}
            footer="Every decision gets logged in the Quarterly Audit tab the same day."
          />
          <SopWeek
            label="Weeks 3 and 4 — Execute"
            owner="whoever was assigned in Week 2"
            body={[
              "Execute the calls. Cancel the tools. Renegotiate the contracts. Right-size the seats. Log every action in the Vendor Detail sidebar so the next quarter has context.",
              "By the end of Week 4, every flagged vendor should have a status of Keep, Watch, or a logged decision.",
            ]}
          />
          <SopWeek
            label="End of Quarter — Freeze"
            owner="Anu"
            body={[
              "Hit \"Freeze Quarter Snapshot\" on the Quarterly Audit tab. This writes the final state of every vendor to the History tab so we have a permanent record of what we decided and why.",
              "Roll into the next quarter.",
            ]}
          />

          <div className="pt-3" style={{ borderTop: '1px solid rgba(20, 27, 77, 0.18)' }}>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70 mb-3">The Three Rules</div>
            <ol className="space-y-3 text-sm leading-relaxed">
              <li><strong>1. Every dollar has an owner.</strong> If no one on the team can name why we're paying for it, it gets canceled.</li>
              <li><strong>2. Keep beats cut.</strong> We're not optimizing for the lowest spend. We're optimizing for the highest-leverage spend. Real growth tools, real coaching, real client gifting all stay.</li>
              <li><strong>3. Decisions get logged the same day they're made.</strong> A decision that isn't logged didn't happen.</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function SopWeek({ label, owner, body, list, footer }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{label}</div>
      <div className="text-sm font-semibold mt-0.5 mb-2 opacity-75">Owner: {owner}</div>
      <div className="space-y-2 text-sm leading-relaxed max-w-3xl">
        {body.map((p, i) => <p key={i}>{p}</p>)}
        {list && (
          <ul className="list-disc pl-5 space-y-1">
            {list.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        )}
        {footer && <p className="opacity-80">{footer}</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header band — matches the visual language of Profit First's top band.
// ---------------------------------------------------------------------------
function Header({ kpis = {}, quarterKey, syncing, onSync, hideTrust, onToggleTrust }) {
  return (
    <div data-testid="ea-header" className="bg-indie-navy text-indie-cream relative overflow-hidden">
      <div className="px-8 pt-7 pb-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-editorial-cream">Expense Audit</div>
            <div className="text-3xl lg:text-4xl font-bold text-white tracking-tight mt-1">
              Every vendor, every dollar, every quarter
            </div>
            <div className="text-sm text-indie-cream/70 mt-3 flex flex-wrap gap-x-5 gap-y-1">
              <span>Current quarter: <span className="font-semibold text-white">{quarterKey || '—'}</span></span>
              <span className="text-indie-cream/40">·</span>
              <span>{hideTrust ? 'Operating spend' : 'Actual spend'}: <span className="font-semibold text-white">{moneyFull(kpis.total_actual_monthly)}/mo</span></span>
              <span className="text-indie-cream/40">·</span>
              <span>Annualized: <span className="font-semibold text-white">{moneyFull(kpis.total_actual_annualized)}</span></span>
              {kpis.non_audit_excluded_monthly > 0 && (
                <>
                  <span className="text-indie-cream/40">·</span>
                  <span
                    className="text-indie-cream/55 italic"
                    title="Auto-excluded: USPTO, owner draws, personal transfers, refunds, CC autopay, cash back"
                  >
                    {moneyFull(kpis.non_audit_excluded_monthly)}/mo non-audit excluded
                  </span>
                </>
              )}
              {hideTrust && kpis.trust_passthrough_monthly > 0 && (
                <>
                  <span className="text-indie-cream/40">·</span>
                  <span className="text-indie-cream/55 italic">{moneyFull(kpis.trust_passthrough_monthly)}/mo trust hidden</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 no-print">
            <button
              type="button"
              onClick={() => onToggleTrust?.(!hideTrust)}
              data-testid="ea-trust-toggle"
              hidden={!hideTrust && (kpis.trust_passthrough_monthly || 0) === 0}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold border transition-colors ${
                hideTrust
                  ? 'bg-indie-yellow text-indie-ink border-indie-yellow hover:bg-indie-yellow/85'
                  : 'bg-indie-cream/10 text-indie-cream border-indie-cream/20 hover:bg-indie-cream/20'
              }`}
              title={hideTrust ? 'Showing operating spend only (trust pass-throughs hidden)' : 'Showing all outflows (including IOLTA / trust pass-throughs)'}
            >
              {hideTrust ? 'Trust hidden ✓' : 'Hide trust'}
            </button>
            <button
              type="button"
              onClick={onSync}
              disabled={syncing}
              data-testid="ea-sync-btn"
              className="inline-flex items-center gap-2 bg-indie-cream/10 hover:bg-indie-cream/20 text-indie-cream border border-indie-cream/20 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold disabled:opacity-50"
            >
              <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync sheets'}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="ea-print-btn"
              className="inline-flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-xs px-3 py-1.5 hover:bg-indie-yellow/85"
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>
      <div className="h-1.5 bg-indie-yellow" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — surfaces the connection instructions for the two sheets.
// ---------------------------------------------------------------------------
function EmptyStatePanel({ serviceAccount }) {
  return (
    <div data-testid="ea-empty-state" className="bg-white border-t-4 border-indie-yellow p-6">
      <div className="label-editorial mb-3">Connect your sheets to unlock the audit</div>
      <p className="text-sm text-indie-ink/75 leading-relaxed mb-4">
        This tab runs on two Google Sheets — both are optional, but you'll get the most value with both connected.
      </p>
      <ol className="space-y-3 text-sm text-indie-ink/80 leading-relaxed">
        <li>
          <span className="font-semibold text-indie-navy">1. Vendor Master sheet</span> — one row per vendor with columns:
          Vendor / Category / Owner / Plan / Cadence / Contracted cost / Renewal date / Seats purchased / Seats active /
          Utilization / Scope / Redundancy notes / ROI notes / Status.
          Share the sheet with <code className="bg-indie-cream px-1 text-[11px]">{serviceAccount || 'service account email'}</code>{' '}
          (Viewer is fine) and set <code className="bg-indie-cream px-1 text-[11px]">VENDOR_MASTER_SPREADSHEET_ID</code> in the backend env.
        </li>
        <li>
          <span className="font-semibold text-indie-navy">2. Expense Detail sheet</span> — one row per QBO transaction with columns:
          Date / Vendor / Category / Amount / Memo. Viktor populates this from QBO's "Transaction List by Vendor" report. Set{' '}
          <code className="bg-indie-cream px-1 text-[11px]">EXPENSE_DETAIL_SPREADSHEET_ID</code>.
        </li>
      </ol>
      <p className="text-xs text-indie-ink/60 mt-4">
        Until those are connected the tab will stay empty. You can still review the structure here — every recurring vendor and
        every QBO charge will flow in automatically once the sheets are live.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subnav — internal tabs across the top.
// ---------------------------------------------------------------------------
function Subnav({ view, onChange }) {
  return (
    <div data-testid="ea-subnav" className="flex flex-wrap gap-1 border-b border-indie-navy/15">
      {SUBVIEWS.map((s) => {
        const Icon = s.icon;
        const active = view === s.key;
        return (
          <button
            type="button"
            key={s.key}
            onClick={() => onChange(s.key)}
            data-testid={`ea-tab-${s.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider font-semibold border-b-2 transition-colors ${
              active
                ? 'border-indie-navy text-indie-navy bg-white'
                : 'border-transparent text-indie-ink/55 hover:text-indie-navy hover:bg-white/50'
            }`}
          >
            <Icon size={14} /> {s.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Audit Dashboard
// ---------------------------------------------------------------------------
function AuditDashboard({ data, onSelectVendor, onGoTo, ai, onDataChanged }) {
  const fetchData = onDataChanged || (() => {});
  const kpis = data?.kpis || {};
  const byCat = data?.by_category || {};
  const opportunities = data?.opportunities || [];
  const quarter = data?.quarter || {};
  const pnlSummary = data?.pnl_summary || {};
  const pnlAvailable = pnlSummary?.available;
  // Prefer AI-structured opportunities when available — these are the
  // human-readable, ranked, vendor-grouped opportunities the AI surfaced in
  // the latest Quarterly Vendor Audit Report.
  const aiOpps = ai?.structured_opportunities || [];
  const hasAiReport = Boolean(ai?.narrative);

  // Only the 5 canonical P&L buckets in the chart; ignore stray subcategories.
  const CANON_BUCKETS = ['leadership', 'marketing', 'sales', 'fulfillment', 'overhead'];
  const catRows = CANON_BUCKETS
    .filter((b) => (byCat[b]?.pnl_monthly_avg || byCat[b]?.actual_monthly || 0) > 0)
    .map((b) => ({
      category: b.charAt(0).toUpperCase() + b.slice(1),
      pnl_monthly_avg: byCat[b]?.pnl_monthly_avg || 0,
      derived_monthly: byCat[b]?.actual_monthly || 0,
      contracted_monthly: byCat[b]?.contracted_monthly || 0,
      gap: byCat[b]?.reconciliation_gap || 0,
      count: byCat[b]?.count || 0,
    }));

  // Reconciliation gap summary line: identify the 2 worst-aligned buckets.
  const gapHotspots = pnlAvailable
    ? [...catRows].sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap)).slice(0, 2)
    : [];

  return (
    <div className="space-y-6">
      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <KpiCard
          label="Total actual spend"
          value={moneyFull(kpis.total_actual_monthly)}
          sub={
            kpis.non_audit_excluded_monthly
              ? `${moneyFull(kpis.total_actual_annualized)}/yr · ${moneyFull(kpis.non_audit_excluded_monthly)}/mo auto-excluded`
              : `${moneyFull(kpis.total_actual_annualized)}/yr · annualized across window`
          }
          testId="ea-kpi-actual"
          accent
        />
        <KpiCard
          label="Contracted recurring"
          value={moneyFull(kpis.total_recurring_monthly)}
          sub={kpis.vendor_count ? `${kpis.vendor_count} vendors in master sheet` : 'no Vendor Master yet'}
          testId="ea-kpi-spend"
        />
        <KpiCard
          label="Unmapped expenses"
          value={kpis.unmapped_count ?? 0}
          sub={kpis.unmapped_actual_monthly ? `${moneyFull(kpis.unmapped_actual_monthly)}/mo to review` : 'all mapped'}
          testId="ea-kpi-unmapped"
        />
        <KpiCard
          label="Renewals next 90 days"
          value={kpis.renewals_next_90d ?? 0}
          sub="negotiation windows"
          testId="ea-kpi-renewals"
        />
        <KpiCard
          label="Avg utilization"
          value={kpis.avg_utilization_pct != null ? pct(kpis.avg_utilization_pct, 0) : '—'}
          sub={kpis.avg_utilization_pct != null && kpis.avg_utilization_pct < 0.6 ? 'below 60% threshold' : 'across all vendors'}
          testId="ea-kpi-util"
        />
      </div>

      {/* Banner — audit due if quarter not frozen */}
      {!quarter.is_frozen && quarter.total_vendors > 0 && (
        <div className="bg-indie-cream border-l-4 border-indie-yellow px-4 py-3 flex items-start gap-3 text-sm text-indie-ink/80">
          <CalendarClock size={16} className="text-indie-yellow flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold text-indie-navy">{quarter.quarter_key} audit is in progress.</span>{' '}
            {quarter.reviewed} of {quarter.total_vendors} vendors reviewed ({pct(quarter.pct, 0)}).
            <button
              type="button"
              onClick={() => onGoTo('workflow')}
              data-testid="ea-banner-continue"
              className="ml-2 underline text-indie-cobalt hover:text-indie-navy"
            >
              Continue audit →
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spend by category */}
        <div data-testid="ea-by-category" className="bg-white border-t-4 border-indie-navy p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="label-editorial">Spend by P&amp;L bucket</div>
              <div className="text-[11px] text-indie-ink/55 mt-0.5">
                {pnlAvailable
                  ? 'Navy = P&L (authoritative). Cobalt = derived from QBO transactions.'
                  : 'Derived from QBO transaction totals. Connect the Monthly P&L sheet for authoritative bucket numbers.'}
              </div>
            </div>
          </div>
          {catRows.length === 0 ? (
            <div className="text-sm text-indie-ink/55 py-8 text-center">
              Connect the Vendor Master sheet to see per-bucket recurring spend.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={catRows} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid {...CHART_GRID} vertical={false} />
                  <XAxis dataKey="category" {...CHART_AXIS} tick={{ fill: '#141B4D', fontSize: 11 }} />
                  <YAxis {...CHART_AXIS} tick={{ fill: '#141B4D', fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => moneyFull(v)} />
                  {pnlAvailable && <Bar dataKey="pnl_monthly_avg" fill="#141B4D" name="P&L (authoritative)" />}
                  <Bar dataKey="derived_monthly" fill="#1D57A5" name="Derived from QBO" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {pnlAvailable && gapHotspots.length > 0 && Math.abs(gapHotspots[0].gap) > 2000 && (
            <div className="mt-3 pt-3 border-t border-indie-navy/10 text-xs text-indie-ink/70">
              <span className="font-semibold text-indie-navy">Reconciliation gap:</span>{' '}
              {gapHotspots.map((g, i) => (
                <span key={g.category}>
                  {i > 0 && ' · '}
                  {g.category}: <span className={g.gap > 0 ? 'text-indie-neg-dark font-semibold' : 'text-indie-cobalt font-semibold'}>
                    {g.gap > 0 ? '−' : '+'}{moneyFull(Math.abs(g.gap))}
                  </span>
                </span>
              ))}
              {' '}— means QBO transactions don't fully roll into the Vendor Master here. Investigate or remap.
            </div>
          )}
        </div>

        {/* Biggest opportunities — sourced from the AI's latest report when
            available, otherwise falls back to the system-computed list. */}
        <div data-testid="ea-opportunities" className="bg-white border-t-4 border-indie-yellow p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="label-editorial">Biggest opportunities</div>
            {hasAiReport && (
              <button
                type="button"
                onClick={() => onGoTo?.('ai')}
                className="text-[10px] uppercase tracking-wider text-indie-cobalt font-semibold hover:text-indie-navy"
                data-testid="ea-opportunities-view-all"
              >
                View full report →
              </button>
            )}
          </div>
          {aiOpps.length > 0 ? (
            <ol className="space-y-2.5">
              {aiOpps.slice(0, 5).map((o, i) => (
                <li key={i}>
                  <div
                    className="bg-indie-cream border-l-4 border-indie-yellow px-3 py-2.5"
                    data-testid={`ea-opportunity-${i}`}
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-indie-ink/55 font-semibold w-5 num-tabular">#{i + 1}</span>
                        <span className="font-semibold text-indie-navy truncate">{o.title}</span>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="font-semibold text-indie-navy num-tabular">{moneyFull(o.annual_impact)}/yr</div>
                        {o.monthly_impact ? (
                          <div className="text-[11px] text-indie-ink/55 num-tabular">{moneyFull(o.monthly_impact)}/mo</div>
                        ) : null}
                      </div>
                    </div>
                    {o.vendors?.length > 0 && (
                      <div className="text-[11px] text-indie-ink/65 ml-7">
                        Vendors: {o.vendors.join(', ')}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 ml-7 mt-1">
                      {o.action && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-indie-cobalt">
                          {o.action}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => onGoTo?.('ai')}
                        className="text-[10px] uppercase tracking-wider text-indie-cobalt hover:text-indie-navy font-semibold"
                        data-testid={`ea-opportunity-reasoning-${i}`}
                      >
                        View reasoning →
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          ) : hasAiReport ? (
            <div className="text-sm text-indie-ink/55 py-8 text-center">
              Claude ran the report but didn't surface any opportunities this quarter — everything's earning its keep.
            </div>
          ) : opportunities.length === 0 ? (
            <div className="text-sm text-indie-ink/55 py-8 text-center" data-testid="ea-opportunities-empty">
              No opportunities yet. Run the Quarterly Vendor Audit from the AI Recommendations tab to surface the top {5} dollar opportunities.
            </div>
          ) : (
            <ol className="space-y-2.5">
              {opportunities.slice(0, 5).map((o, i) => {
                const palette = STATUS_COLORS[o.suggested_status] || STATUS_COLORS.Watch;
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => onSelectVendor(o)}
                      className="w-full text-left bg-indie-cream border-l-4 border-indie-yellow px-3 py-2.5 hover:bg-indie-cream/70 transition-colors"
                      data-testid={`ea-opportunity-${i}`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-indie-ink/55 font-semibold w-5">#{i + 1}</span>
                          <span className="font-semibold text-indie-navy truncate">{o.vendor_name}</span>
                          <span className={`inline-flex items-center gap-1 px-1.5 text-[10px] uppercase tracking-wider font-semibold ${palette.bg} ${palette.text}`}>
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${palette.dot}`} />
                            {o.suggested_status}
                          </span>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <div className="font-semibold text-indie-navy num-tabular">{moneyFull(o.opportunity_monthly)}/mo</div>
                          <div className="text-[11px] text-indie-ink/55 num-tabular">{moneyFull(o.opportunity_monthly * 12)}/yr</div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>

      {/* Unmapped charges callout */}
      {(data?.unmapped?.length || 0) > 0 && (
        <Callout title="Unmapped charges — every expense without a Vendor Master row" tone="warn">
          {data.unmapped.length} payee{data.unmapped.length === 1 ? '' : 's'} pulling money out of QuickBooks without a
          corresponding Vendor Master entry — ranked by total impact across the visible window. Recurring shows a true
          monthly run rate; one-time and sporadic charges show their actual amount and date so a single big hit doesn't
          look like a subscription.
          <ul className="mt-3 space-y-1 text-sm max-h-96 overflow-y-auto pr-1">
            {data.unmapped.slice(0, 30).map((u) => {
              const spend = formatSpend(u);
              const cadenceTone = u.cadence === 'recurring'
                ? 'text-indie-evergreen'
                : u.cadence === 'one_time'
                ? 'text-indie-ink/55 italic'
                : 'text-indie-cobalt';
              return (
                <li key={u.id} className="flex items-center justify-between gap-3 py-1 border-b border-indie-navy/5">
                  <span className="min-w-0 truncate">
                    <span className="font-semibold text-indie-navy">{u.vendor_name}</span>
                    {u.category && <span className="text-[11px] uppercase tracking-wider text-indie-ink/55 ml-2">{u.category}</span>}
                  </span>
                  <span className="flex items-center gap-3 whitespace-nowrap">
                    <span className="num-tabular text-indie-ink/85 text-right">
                      <span className={`font-semibold ${cadenceTone}`}>{spend.primary}</span>
                      <span className="text-[11px] text-indie-ink/50 ml-2">{spend.secondary}</span>
                    </span>
                    <ReassignUnmappedRow qboName={u.vendor_name} vendors={data.vendors || []} onSaved={fetchData} />
                  </span>
                </li>
              );
            })}
          </ul>
          {data.unmapped.length > 30 && (
            <div className="mt-2 text-xs text-indie-ink/55">
              Showing top 30 of {data.unmapped.length}. Open the Vendors tab to see all and filter by category.
            </div>
          )}
        </Callout>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2) Vendors view — sortable, filterable table + detail drawer
// ---------------------------------------------------------------------------
function VendorsView({ data, selectedVendor, setSelectedVendor, onDecisionSaved,
                      auditMode = false, headerSlot = null }) {
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState(auditMode ? 'unreviewed' : 'all');
  const [renewingSoon, setRenewingSoon] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(auditMode ? 'opportunity_monthly' : 'opportunity_monthly');
  const [sortDir, setSortDir] = useState('desc');

  const vendors = data?.vendors || [];
  const unmapped = (data?.unmapped || []).map((u) => ({ ...u, is_unmapped: true, suggested_status: 'Unmapped', final_status: 'Unmapped' }));
  // Audit mode is decision-focused: hide pure-Keep vendors and unmapped charges.
  const all = auditMode
    ? vendors.filter((v) => (v.suggested_status || 'Watch') !== 'Keep')
    : [...vendors, ...unmapped];

  const filtered = useMemo(() => {
    let rows = all;
    if (filterCat !== 'all') rows = rows.filter((v) => v.category === filterCat);
    if (filterStatus === 'unreviewed') {
      rows = rows.filter((v) => !v.manual_override && !v.current_decision);
    } else if (filterStatus === 'reviewed') {
      rows = rows.filter((v) => v.manual_override || v.current_decision);
    } else if (filterStatus !== 'all') {
      rows = rows.filter((v) => (v.final_status || v.suggested_status) === filterStatus);
    }
    if (renewingSoon) rows = rows.filter((v) => v.renewal_days_away != null && v.renewal_days_away <= 90);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((v) => v.vendor_name?.toLowerCase().includes(q) || v.owner?.toLowerCase().includes(q));
    }
    rows = [...rows].sort((a, b) => {
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * (sortDir === 'asc' ? 1 : -1);
    });
    return rows;
  }, [all, filterCat, filterStatus, renewingSoon, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const cats = Array.from(new Set(all.map((v) => v.category).filter(Boolean))).sort();

  if (all.length === 0) {
    return (
      <div className="bg-white border-t-4 border-indie-navy p-8 text-center text-sm text-indie-ink/65">
        No vendors yet. Sync the Vendor Master sheet to populate this table.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-5">
      <div className="bg-white border-t-4 border-indie-navy">
        {/* Optional gamified header injected by audit mode */}
        {headerSlot}
        {/* Filters */}
        <div className="px-5 py-4 flex flex-wrap items-center gap-3 border-b border-indie-navy/10">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-indie-ink/50" />
            <input
              type="text"
              placeholder="Search vendor or owner"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="ea-vendor-search"
              className="bg-indie-cream border border-indie-navy/15 pl-8 pr-3 py-1.5 text-sm w-56 focus:outline-none focus:border-indie-navy"
            />
          </div>
          <FilterSelect label="Category" value={filterCat} onChange={setFilterCat} options={[['all', 'All'], ...cats.map((c) => [c, c.charAt(0).toUpperCase() + c.slice(1)])]} testId="ea-filter-cat" />
          <FilterSelect
            label="Status"
            value={filterStatus}
            onChange={setFilterStatus}
            options={auditMode
              ? [['unreviewed', 'Needs decision'], ['reviewed', 'Reviewed'], ['all', 'All flagged'], ...DECISION_OPTIONS.map((s) => [s, s])]
              : [['all', 'All'], ...DECISION_OPTIONS.map((s) => [s, s]), ['Unmapped', 'Unmapped']]}
            testId="ea-filter-status"
          />
          <label className="inline-flex items-center gap-2 text-xs text-indie-ink/75 cursor-pointer">
            <input
              type="checkbox"
              checked={renewingSoon}
              onChange={(e) => setRenewingSoon(e.target.checked)}
              data-testid="ea-filter-renewing"
              className="accent-indie-cobalt"
            />
            Renewing in 90 days
          </label>
          <div className="ml-auto text-xs text-indie-ink/55">{filtered.length} of {all.length}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-indie-navy text-indie-cream">
              <tr>
                <Th onClick={() => toggleSort('vendor_name')} active={sortKey === 'vendor_name'} dir={sortDir}>Vendor</Th>
                <Th onClick={() => toggleSort('category')} active={sortKey === 'category'} dir={sortDir}>Category</Th>
                <Th onClick={() => toggleSort('contracted_monthly')} active={sortKey === 'contracted_monthly'} dir={sortDir} right>Contracted</Th>
                <Th onClick={() => toggleSort('actual_monthly')} active={sortKey === 'actual_monthly'} dir={sortDir} right>Actual</Th>
                <Th onClick={() => toggleSort('utilization_pct')} active={sortKey === 'utilization_pct'} dir={sortDir} right>Util</Th>
                <Th onClick={() => toggleSort('renewal_days_away')} active={sortKey === 'renewal_days_away'} dir={sortDir} right>Renews</Th>
                <Th onClick={() => toggleSort('opportunity_monthly')} active={sortKey === 'opportunity_monthly'} dir={sortDir} right>Opp/mo</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const status = v.final_status || v.suggested_status || 'Watch';
                const palette = STATUS_COLORS[status] || STATUS_COLORS.Watch;
                const selected = selectedVendor?.id === v.id;
                return (
                  <tr
                    key={v.id}
                    onClick={() => setSelectedVendor(v)}
                    data-testid={`ea-vendor-row-${v.id}`}
                    className={`border-b border-indie-navy/5 cursor-pointer hover:bg-indie-cream/40 ${selected ? 'bg-indie-yellow/15' : ''}`}
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-indie-navy">{v.vendor_name}</div>
                      {v.owner && <div className="text-[11px] text-indie-ink/55">{v.owner}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-indie-ink/70 text-xs uppercase tracking-wider">{v.category || '—'}</td>
                    <td className="px-3 py-2.5 text-right num-tabular text-indie-ink/85">{v.contracted_monthly ? moneyFull(v.contracted_monthly) : '—'}</td>
                    <td className="px-3 py-2.5 text-right num-tabular text-indie-ink/85">
                      {(() => {
                        const s = formatSpend(v);
                        if (!v.total_amount) return '—';
                        const tone = v.cadence === 'recurring' ? '' : v.cadence === 'one_time' ? 'italic text-indie-ink/70' : 'text-indie-cobalt';
                        return (
                          <span className="inline-block text-right">
                            <span className={`block font-medium ${tone}`}>{s.primary}</span>
                            <span className="block text-[10px] text-indie-ink/50">{s.secondary}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2.5 text-right num-tabular">
                      {v.utilization_pct != null ? (
                        <span className={v.utilization_pct < 0.6 ? 'text-indie-neg-dark' : 'text-indie-ink/85'}>
                          {pct(v.utilization_pct, 0)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs">
                      {v.renewal_days_away != null ? (
                        <span className={v.renewal_days_away <= 30 ? 'text-indie-neg-dark font-semibold' : v.renewal_days_away <= 90 ? 'text-indie-yellow font-semibold' : 'text-indie-ink/65'}>
                          {v.renewal_days_away}d
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right num-tabular text-indie-ink/85">
                      {v.opportunity_monthly ? moneyFull(v.opportunity_monthly) : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${palette.bg} ${palette.text}`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${palette.dot}`} />
                          {status}
                        </span>
                        {v.manual_override && (
                          <span
                            title={`Manual override by ${v.manual_override.set_by_name || 'team'}`}
                            data-testid={`ea-vendor-row-manual-${v.id}`}
                            className="text-[9px] uppercase tracking-wider font-semibold text-indie-yellow bg-indie-ink px-1 py-0.5"
                          >
                            M
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Passthrough & System — muted section. Always visible but excluded
            from KPIs, opportunities, and quarterly audit. Helps the team
            verify the full picture without polluting the actionable list. */}
        {!auditMode && <PassthroughSection passthroughs={data?.passthroughs || []} />}
      </div>

      {/* Detail panel */}
      <div className="lg:sticky lg:top-4 self-start">
        <VendorDetail vendor={selectedVendor} onDecisionSaved={onDecisionSaved} quarterKey={data?.current_quarter_key} />
      </div>
    </div>
  );
}

function PassthroughSection({ passthroughs }) {
  const [expanded, setExpanded] = useState(false);
  if (!passthroughs.length) return null;
  const total = passthroughs.reduce((s, p) => s + (p.actual_monthly || 0), 0);
  const grouped = passthroughs.reduce((acc, p) => {
    const k = p.passthrough_category || 'other';
    (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});
  return (
    <div className="border-t border-indie-navy/10 mt-2 bg-indie-cream/30" data-testid="ea-passthrough-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-indie-cream/60 text-left"
        data-testid="ea-passthrough-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-indie-ink/60 bg-indie-ink/10 px-2 py-0.5">
            Passthrough &amp; System
          </span>
          <span className="text-xs text-indie-ink/65">
            {passthroughs.length} payees · <span className="num-tabular font-semibold">{moneyFull(total)}/mo</span> excluded from audit
          </span>
        </div>
        <span className="text-[10px] text-indie-cobalt uppercase tracking-wider font-semibold">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>
      {expanded && (
        <div className="px-5 pb-4 space-y-4">
          {Object.keys(grouped).sort((a, b) =>
            (grouped[b].reduce((s, p) => s + p.actual_monthly, 0)) -
            (grouped[a].reduce((s, p) => s + p.actual_monthly, 0))
          ).map((cat) => {
            const rows = grouped[cat].sort((a, b) => b.actual_monthly - a.actual_monthly);
            const catTotal = rows.reduce((s, p) => s + p.actual_monthly, 0);
            return (
              <div key={cat}>
                <div className="text-[10px] uppercase tracking-wider text-indie-ink/55 font-semibold mb-1 flex justify-between">
                  <span>{PASSTHROUGH_LABELS[cat] || cat}</span>
                  <span className="num-tabular">{moneyFull(catTotal)}/mo · {rows.length} payees</span>
                </div>
                <div className="space-y-0.5">
                  {rows.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-xs text-indie-ink/65 py-1 border-b border-indie-navy/5 last:border-0">
                      <div className="min-w-0 truncate">
                        <span className="font-medium text-indie-ink/85">{p.vendor_name}</span>
                        <span className="ml-1.5 text-indie-ink/45 text-[10px]">{p.non_audit_reason}</span>
                      </div>
                      <div className="num-tabular whitespace-nowrap text-indie-ink/85">
                        {moneyFull(p.actual_monthly)}/mo · {p.txn_count} txns
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}




// ---------------------------------------------------------------------------
// 3) Quarterly Audit workflow
// ---------------------------------------------------------------------------
function QuarterlyAuditView({ data, selectedVendor, setSelectedVendor, onDecisionSaved, onFreeze }) {
  const [freezing, setFreezing] = useState(false);
  const quarter = data?.quarter || {};
  const vendors = data?.vendors || [];

  // "Needs decision" = anything not auto-Keep that has no manual override AND
  // no quarterly decision logged. Matches the audit-mode filter default.
  const actionable = vendors.filter((v) => (v.suggested_status || 'Watch') !== 'Keep');
  const reviewed = actionable.filter((v) => v.manual_override || v.current_decision);
  const remaining = actionable.length - reviewed.length;
  const pct = actionable.length ? Math.round((reviewed.length / actionable.length) * 100) : 0;

  const handleFreeze = async () => {
    if (!window.confirm(`Freeze ${quarter.quarter_key} audit snapshot? Decisions can still be edited but the snapshot is preserved in History.`)) return;
    setFreezing(true);
    try {
      await api.post(`/expense-audit/quarter/${quarter.quarter_key}/freeze`);
      toast.success(`${quarter.quarter_key} audit frozen.`);
      onFreeze?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Freeze failed.');
    } finally {
      setFreezing(false);
    }
  };

  const header = (
    <div className="px-5 py-4 border-b border-indie-navy/10 bg-indie-cream/30" data-testid="ea-audit-header">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="label-editorial">{quarter.quarter_key} audit workflow</div>
          <div className="text-2xl font-bold text-indie-navy mt-1 num-tabular">
            {reviewed.length} reviewed · <span className={remaining === 0 ? 'text-indie-evergreen' : 'text-indie-yellow'}>{remaining} to go</span>
          </div>
          <div className="text-xs text-indie-ink/65 mt-0.5">
            of {actionable.length} flagged vendors (Keep-status vendors auto-pass)
          </div>
        </div>
        <button
          type="button"
          onClick={handleFreeze}
          disabled={freezing || actionable.length === 0}
          data-testid="ea-freeze-btn"
          className="bg-indie-cobalt text-white text-xs uppercase tracking-wider font-semibold px-3 py-2 hover:bg-indie-navy disabled:opacity-50"
        >
          {freezing ? 'Freezing…' : (quarter.is_frozen ? 'Re-freeze snapshot' : 'Freeze quarter snapshot')}
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-2 bg-indie-cream mt-3 relative">
        <div className="h-2 bg-indie-evergreen transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-indie-ink/55 mt-1 num-tabular">{pct}% complete</div>
    </div>
  );

  if (actionable.length === 0) {
    return (
      <div className="space-y-5">
        {header.props.children && (
          <div className="bg-white border-t-4 border-indie-evergreen">{header}</div>
        )}
        <div className="bg-white border-t-4 border-indie-navy p-8 text-center text-sm text-indie-ink/65">
          No flagged vendors this quarter — everything is on track.
        </div>
      </div>
    );
  }

  return (
    <VendorsView
      data={data}
      selectedVendor={selectedVendor}
      setSelectedVendor={setSelectedVendor}
      onDecisionSaved={onDecisionSaved}
      auditMode={true}
      headerSlot={header}
    />
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// ReassignUnmappedRow — inline dropdown that hard-maps a QBO payee name onto
// a specific Vendor Master row so the txns route to the right bucket next
// sync. Persists via /api/expense-audit/reassignments.
// ---------------------------------------------------------------------------
function ReassignUnmappedRow({ qboName, vendors, onSaved }) {
  const [saving, setSaving] = useState(false);
  const options = useMemo(
    () => [...(vendors || [])].sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || '')),
    [vendors],
  );
  const handlePick = async (e) => {
    const targetId = e.target.value;
    if (!targetId) return;
    setSaving(true);
    try {
      await api.post('/expense-audit/reassignments', {
        qbo_vendor_name: qboName,
        target_vendor_id: targetId,
      });
      toast.success(`Reassigned "${qboName}" — will apply on next sync.`);
      onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save reassignment.';
      toast.error(msg);
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };
  return (
    <select
      onChange={handlePick}
      disabled={saving}
      data-testid={`ea-reassign-${qboName.replace(/\s+/g, '-').toLowerCase()}`}
      defaultValue=""
      className="text-[11px] bg-white border border-indie-navy/20 px-1.5 py-1 uppercase tracking-wider font-semibold text-indie-navy hover:border-indie-navy disabled:opacity-50"
      title="Reassign this QBO payee to an existing vendor"
    >
      <option value="">{saving ? 'Saving…' : 'Reassign…'}</option>
      {options.map((v) => (
        <option key={v.id} value={v.id}>{v.vendor_name}</option>
      ))}
    </select>
  );
}
